import { z } from 'zod'
import { asEnumType, asObjectType } from '@gqloom/zod'
import { templateCreditSessionTypeSchema } from '../payments/service-schemas'

export const packageStatusSchema = z
  .enum([
    'active',
    'archived',
    'completed',
    'cancelled',
    'cancellationScheduled',
    'pendingStartDate',
    'suspended',
    'expired',
  ])
  .register(asEnumType, { name: 'PackageStatus' })
export type PackageStatus = z.infer<typeof packageStatusSchema>

export const packageTypeSchema = z
  .enum(['membership', 'package', 'classPack'])
  .register(asEnumType, { name: 'PackageType' })
export type PackageType = z.infer<typeof packageTypeSchema>

export const packageSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    status: packageStatusSchema,
    type: packageTypeSchema,
    // Home site that owns this service. Used to detect services surfaced here only
    // because they are shared into the current (corporate-linked) location.
    siteId: z.string().optional(),
    // Cross-location waiting period (days from the current cycle start) before this
    // service's credits can be used at a shared location. Null/0 = no waiting period.
    minimumHomeUsageInDays: z.number().nullish(),
    // Date this service's credits become usable at a shared location for the current cycle.
    // Computed server-side from the authoritative allocation table. Null = no waiting period.
    sharedHomeUsageUnlockDate: z.string().nullish(),
    // Cross-location sharing config. On the home location's card these flag that the
    // service is shared OUT to other sites. isAllSharedSites = shared to every site.
    isAllSharedSites: z.boolean().nullish(),
    sharedSiteIds: z.array(z.string()).nullish(),
    commitment: z.string(),
    packageNumber: z.string(),
    startDate: z.string(),
    isActivationPending: z.boolean().optional(),
    referredBy: z.string().optional(),
    price: z.number(),
    currency: z.string().nullable().optional(),
    billingFrequency: z.string(),
    creditsUsed: z.number().optional(),
    creditsTotal: z.number().optional(),
    isRefunded: z.boolean().optional(),
    cancellationDate: z.string().optional(),
    cancellationNote: z.string().optional(),
    // Credit allocation per session type
    creditAllocations: z
      .array(
        z.object({
          sessionTypeId: z.string(),
          siteSessionTypeId: z.uuid().optional(),
          siteSessionTypeIds: z.array(z.uuid()).optional(),
          sessionTypeName: z.string(),
          creditsTotal: z.number(),
          creditsUsed: z.number(),
          expiresAt: z.string().optional(),
          isUnlimited: z.boolean().optional(),
          activateOnFirstUse: z.boolean().optional(),
          validFrom: z.string().nullish(),
          validTo: z.string().nullish(),
        })
      )
      .optional(),
    // Secondary fees
    hasSecondaryFees: z.boolean().optional(),
    secondaryFeeName: z.string().optional(),
    // State-specific dates
    canceledAt: z.string().optional(),
    suspendedAt: z.string().optional(),
    completedAt: z.string().optional(),
    scheduledCancellationDate: z.string().optional(),
    scheduledCancellationId: z.string().optional(),
    endDate: z.string().optional(),
    createdByInvoiceId: z.string().optional(),
    onHold: z.boolean().optional(),
    activeSuspension: z.object({ endDate: z.string().nullish() }).optional(),
    scheduledPlanChange: z
      .object({
        servicePlanChangeId: z.string(),
        status: z.string(),
        effectiveDate: z.string(),
        toServiceTemplateId: z.string(),
        toServiceTemplateName: z.string().optional(),
      })
      .optional(),
    changePlanEnabled: z.boolean().optional(),
    lastExecutedPlanChange: z
      .object({
        servicePlanChangeId: z.string(),
        fromServiceTemplateName: z.string().nullable().optional(),
        executedAt: z.string().nullable().optional(),
        initiatedByStaffName: z.string().nullable().optional(),
      })
      .optional(),
    nextBillingDate: z.string().optional(),
    billingCycleAmount: z.number().optional(),
    billingCycleUnit: z.string().optional(),
    templateCreditSessionTypes: z.array(templateCreditSessionTypeSchema).optional(),
  })
  .register(asObjectType, { name: 'Package' })

export type Package = z.infer<typeof packageSchema>

export const packageFiltersSchema = z.object({
  dateRange: z
    .object({
      startDate: z.string().nullable(),
      endDate: z.string().nullable(),
    })
    .optional(),
  type: z.union([packageTypeSchema, z.literal('all')]).optional(),
  status: z.union([packageStatusSchema, z.literal('all')]).optional(),
  searchQuery: z.string().optional(),
})

export type PackageFilters = z.infer<typeof packageFiltersSchema>
